<template>
    <div>
        <loading-indicator :clearBackground="true" :fullParent="false" :isLoading="isLoading" />
        <div v-if="paging && sortedCoupons.length > 0">
            <div class="text-right paging-information d-flex justify-content-end align-items-center">
                <span>
                    {{$t('paging_information_entry', this.paging)}}
                </span>
                <span v-if="this.paging.last_page > 0">
                    &nbsp;{{$t('paging_information_page', this.paging)}}
                </span>
            </div>
            <pagination :paging="paging" :is-loading="isLoading" @pageClick="(page) => {this.loadPage(page)}"/>
        </div>
        <table v-if="sortedCoupons.length" class="w-100 ding-table" border=0>
            <thead>
                <th v-if="!customerId" class="d-none d-sm-table-cell"></th>
                <th class="min-100">
                    {{$t('title')}}
                </th>
                <th v-if="showDescription" class="d-none d-sm-table-cell min-100">{{$t('description')}}</th>
                <th class="d-none d-sm-table-cell">{{$t('expired_at')}}</th>
                <th>{{$t('discount_amount')}}</th>
                <th>{{$t('status')}}</th>
            </thead>
            <tbody>
                <tr v-for="c in sortedCoupons" :key="c.id" :class="{'dark-background': !customerId}" class="rounded-0" @click="selectedCoupon = c">
                    <td v-if="!customerId" class="d-none d-sm-table-cell">
                        <div
                            v-if="c.offer.image?.file_name"
                            class="img-container"
                            :style="{background: 'url(' + context.storageEndpoint + c.offer.image.file_name + ') center/cover no-repeat' }">
                            <img :src="context.storageEndpoint + c.offer.image?.file_name" style="visibility: hidden;" />
                        </div>
                    </td>
                    <td>
                        {{ c.offer.title }}
                        <div>
                            <span v-if="c.offer.applicable_at_counter" class="label label-tight label-muted d-none d-sm-inline-block mx-0">
                                {{ $t('applicable_at_counter') }}
                            </span>
                        </div>
                    </td>
                    <td v-if="showDescription" class="d-none d-sm-table-cell white-space-pre-line">{{ c.offer.description || '' }}</td>
                    <td class="d-none d-sm-table-cell">
                        <div v-if="c.offer.start && c.expired_at">
                            <span>{{ formatDate(c.offer.start) }}</span>
                            <span>{{ $t('general.to') }}</span>
                            <div>{{ formatDate(c.expired_at) }}</div>
                        </div>
                        <div v-else>{{ $t('no_expired_date') }}</div>
                    </td>
                    <td class="blue-highlight">
                        <div>
                            {{ formatValue(c.offer) }}
                        </div>
                        <div v-if="getMinOrderAmountText(c.offer)" class="text-muted">
                            <small>{{ getMinOrderAmountText(c.offer) }}</small>
                        </div>
                    </td>
                    <td>
                        <div class="label text-nowrap" :class="classForCoupon(c)" v-b-tooltip.hover.bottom :title="c.booking_display_id ? $t('booking_display_id', {displayId: c.booking_display_id}) : ''" >{{ $t('coupon_status_' +c.status) }}</div>
                        <div v-if="c.last_redeemed_at" class="text-muted">
                            <small>{{ $t('redeemed_at', {redeemedDate: formatDate(c.last_redeemed_at)}) }}</small>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
        <div v-else class="text-center text-muted pt-4">
            <small>{{ $t('no_coupons') }}</small>
        </div>
        <customer-coupon-detail-modal
            v-if="selectedCoupon"
            :coupon="selectedCoupon"
            :provider="provider"
            :context="context"
            @close="selectedCoupon = null"
            @update-coupon="onCouponUpdated"
        />
    </div>
</template>

<script>
import api from '../../mixins/api';
import promptConfirm from '../../mixins/prompt-confirm';
import loadingIndicator from '../../components/common/LoadingIndicator';
import CustomerCouponDetailModal from './CustomerCouponDetailModal.vue';
import moment from 'moment';
import 'moment-timezone';
import { mapGetters } from 'vuex';
import { localizedAmount } from '../../currency';

export default {
    props: {
        customerId: { required: false }, // 如果是廠商後台看的話，會直接指名 customerId
        provider: { type: Object, required: true },
        context: { type: Object, required: true },
    },
    components: { loadingIndicator, CustomerCouponDetailModal },
    mixins: [api, promptConfirm],
    mounted() {
        this.loadCoupons(1);
    },
    data() {
        return {
            isLoading: false,
            coupons: [],
            paging: null,
            selectedCoupon: null
        }
    },
    computed: {
        ...mapGetters({
            currency: 'currencies/currency',
            currencyRateMap: 'currencies/currencyRateMap',
            currencyOptions: 'currencies/currencyOptions',
        }),
        showDescription() {
            return !this.customerId;
        },
        sortedCoupons() {
            return this.coupons.sort((a, b) => {
                if ((a.status == 'active' || a.status == 'redeemed') && (b.status === 'disabled' || a.status == 'expired')) {
                    return -1;
                }
            })
        }
    },
    methods: {
        formatDate(date) {
            return moment.tz(date, 'GMT').tz(this.provider.timezone).format('YYYY-MM-DD');
        },
        formatValue(offer) {
            if (this.customerId) {
                if (offer.rule_type == 'FIXED_AMOUNT') {
                    return '-$' + offer.value;
                } else if (offer.rule_type == 'PERCENTAGE') {
                    return '-' + offer.value + '%';
                }
            } else {
                if (offer.rule_type == 'FIXED_AMOUNT') {
                    return this.$t('fixed_amount_text', { value: localizedAmount(offer.value, this.provider.currency) });
                } else if (offer.rule_type == 'PERCENTAGE') {
                    const isZh = getSharedLang() === 'zh';
                    let value = offer.value;
                    if (isZh) {
                        value = 100 - offer.value;
                    }
                    if (value % 10 == 0) {
                        value /= 10;
                    }
                    return this.$t('percentage_text', {value});
                }
            }
        },
        getMinOrderAmountText(offer) {
            const minOrderAmountCondition = offer.conditions.find(item => item.type == 'MIN_ORDER_AMOUNT');
            if (minOrderAmountCondition) {
                const extras = JSON.parse(minOrderAmountCondition.extras);
                return this.$t('min_order_amount_text', { value: localizedAmount(extras.min, this.provider.currency) })
            } else {
                return null;
            }
        },
        classForCoupon(coupon) {
            const labelVariant = {
                active: !this.customerId ? 'customer-label label-success' : 'label-success',
                expired: !this.customerId ? 'customer-label label-danger' : 'label-danger',
                redeemed: !this.customerId ? 'customer-label label-muted' :  'label-muted',
                disabled: !this.customerId ? 'customer-label label-danger' :  'label-danger'
            }[coupon.status.toLowerCase()]
            return {[labelVariant]: true}
        },
        loadCoupons(page) {
            if (!this.customerId) {
                this.isLoading = true;
                this.getCustomerAllCoupons((response, error) => {
                    this.isLoading = false;
                    if (error) {
                        this.promptAlert(error);
                    } else {
                        this.coupons = response.data;
                    }
                })
            } else if (page) {
                this.loadPage(page);
            }
        },
        loadPage(page) {
            this.isLoading = true;
            this.getCustomerCouponsByAdmin(this.customerId, page, (response, error) => {
                this.isLoading = false;
                if (error) {
                    this.promptAlert(error);
                } else {
                    this.coupons = response.data.data;
                    this.paging = response.data;
                }
            })
        },
        onCouponUpdated(coupon) {
            this.selectedCoupon = null;
            const page = this.paging?.current_page;
            this.loadCoupons(page);
        },
    }
}
</script>

<style scoped lang="scss">
    .ding-table {
        table-layout: auto;
        th {
            white-space: nowrap;
        }

        tr {
            cursor: pointer;
        }
    }

    .dark-background {
        background-color: #F6F6F6;
    }

    .label {
        font-size: 13.33333px;
        padding: 7px 8px;
        margin-top: 4px;
    }

    .label.customer-label {
        background-color: transparent;
        &.label-danger {
            border: 1px solid #f7473f;
            color: #f7473f;
        }

        &.label-success {
            border: 1px solid #3FCC53;
            color: #3FCC53;
        }

        &.label-muted {
            border: 1px solid #969696;
            color: #969696;
        }
    }

    .blue-highlight {
        color: #0094FB;
    }

    .paging-information {
        font-size: 12px;
        margin: 16px 0px;
        color: #90A4AE;
    }

    .min-100 {
        min-width: 100px;
    }

    td:has(> .img-container) {
        max-width: 100px;
    }

    .img-container {
        width: 100%;

        img {
            width: 100%;
        }
    }

</style>

<i18n>
{
    "en": {
        "no_coupons": "No coupons",
        "no_expired_date": "Permanent",
        "title": "Title",
        "expired_at": "Expired Date",
        "discount_amount": "Discount",
        "description": "Description",
        "status": "Status",
        "coupon_status_active": "Active",
        "coupon_status_expired": "Expired",
        "coupon_status_redeemed": "Redeemed",
        "coupon_status_disabled": "Disabled",
        "redeemed_at": "Redeemed at {redeemedDate}",
        "paging_information_entry": "Total {total} coupons",
        "paging_information_page": "({current_page} / {last_page} pages)",
        "fixed_amount_text": "{value} off",
        "percentage": "{value}% off",
        "min_order_amount_text": "Applicable when over {value}",
        "coupon_type_percent_off_zh": "{offPercentage} Off",
        "booking_display_id": "Booking ID {displayId}",
        "applicable_at_counter": "Redeemable At Counter"
    },
    "zh": {
        "no_coupons": "目前沒有優惠券",
        "no_expired_date": "永久有效",
        "title": "標題",
        "expired_at": "有效期限",
        "discount_amount": "折扣",
        "description": "使用說明",
        "status": "狀態",
        "coupon_status_active": "有效",
        "coupon_status_expired": "已過期",
        "coupon_status_redeemed": "已使用",
        "coupon_status_disabled": "停用",
        "redeemed_at": "於 {redeemedDate} 使用",
        "paging_information_entry": "共 {total} 張",
        "paging_information_page": " ({current_page}/{last_page} 頁)",
        "fixed_amount_text": "折 {value} 元",
        "percentage_text": "{value} 折",
        "min_order_amount_text": "滿 {value} 即可使用",
        "coupon_type_percent_off_en": "{offPercentage} OFF",
        "booking_display_id": "預約編號 {displayId}",
        "applicable_at_counter": "現場核銷"
    }
}
</i18n>